import { PatientEncounter } from "@finpay-development/shared-platform/lib/esm/patient/components/models/patient-encounter";
import { PatientEncounterCard } from "@finpay-development/shared-platform/lib/esm/patient/components/models/patient-encounter-card";
import { WorkflowId } from "@finpay-development/shared-platform/lib/esm/patient/components/misc/patient-status";
import {
  patientHelper,
  patientService,
} from "@finpay-development/shared-platform/lib/esm/patient/services/patient-service";
import { axiosReadHelper } from "@finpay-development/shared-platform/lib/esm/shared/service/axios-read-helper";
import { IocQueryDefinition } from "./ioc-queries";
import { mapPeToCard } from "./map-pe-to-card";

export interface LoadIocQueryResult {
  cards: PatientEncounterCard[];
  errorMessage: string | null;
}

async function fetchEncounterKeys(workflowStatusIds?: string): Promise<{
  ids: number[];
  errorMessage: string | null;
}> {
  // Keys Joi schema requires queryStringParameters to be an object — API Gateway
  // sends null when there is no query string (400: must be of type object).
  // isConverted without clientId is accepted but not applied as a filter.
  const params = new URLSearchParams();
  if (workflowStatusIds) {
    params.set("workflowStatusIds", workflowStatusIds);
  } else {
    params.set("isConverted", "false");
  }
  const response = await axiosReadHelper({
    dataId: 0,
    url: `patient-encounter/v2/patient/encounter/keys?${params.toString()}`,
  });

  if (response.hasErrors) {
    return {
      ids: [],
      errorMessage: response.errorMessage || "Failed to load encounter keys",
    };
  }

  const entity = response.entity;
  const ids = Array.isArray(entity)
    ? entity
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    : [];

  return { ids, errorMessage: null };
}

async function fetchFullPeById(
  patientEncounterId: number
): Promise<PatientEncounter | null> {
  // getPatientInstanceOfCare uses encounterId as the path id; patientId is cache-key only
  const response = await patientService.getPatientInstanceOfCare({
    patientId: 0,
    encounterId: patientEncounterId,
  });

  if (response.hasErrors || !response.entity) {
    return null;
  }

  return response.entity as PatientEncounter;
}

async function fetchEncounterContact(patientEncounterId: number) {
  const response = await patientService.getContactInfo(0, patientEncounterId);
  if (response.hasErrors || !response.entity) {
    return null;
  }
  return response.entity;
}

async function fetchPeAndContact(patientEncounterId: number) {
  const [pe, contact] = await Promise.all([
    fetchFullPeById(patientEncounterId),
    fetchEncounterContact(patientEncounterId),
  ]);
  if (!pe) {
    return null;
  }
  return { pe, contact };
}

export async function loadIocQuery(
  query: IocQueryDefinition
): Promise<LoadIocQueryResult> {
  const keysResult = await fetchEncounterKeys(query.workflowStatusIds);
  if (keysResult.errorMessage) {
    return { cards: [], errorMessage: keysResult.errorMessage };
  }

  const ids = keysResult.ids.slice(0, query.limit);
  if (ids.length === 0) {
    return { cards: [], errorMessage: null };
  }

  const peResults = await Promise.all(ids.map((id) => fetchPeAndContact(id)));
  const cards = peResults
    .filter(
      (row): row is { pe: PatientEncounter; contact: any } => row != null
    )
    .map(({ pe, contact }) => mapPeToCard(pe, contact));

  return {
    cards: patientHelper.setStatusColor(cards, WorkflowId.Preconverted),
    errorMessage: null,
  };
}
